import { TOKEN_KEY } from "../../api/credentials.ts";
import { getUrls } from "../../api/openapi.ts";
import { SyntenyTypeEnum, TrackEnum } from "../../openapi";
import { useGenomeViewerStore } from "../../store/store.ts";
import type {
  WorkerMessageGene,
  WorkerMessageNUFeature,
  WorkerMessageProtein,
  WorkerMessageRenderBase,
  WorkerMessageSequence,
} from "../../track-workers/types.ts";
import type { PileupRenderingMode, Track, TrackGroup } from "../../types.ts";
import { initRenderBaseMessageData } from "../utils.ts";
import { sendSyntenyRenderMessage } from "./post-alignment-messages.ts";

// At the moment, we redraw all tracks to always have height and synteny data up to date with the new pileup.
// Looks like it's fast enough to render the imageBitmap.
// If we don't draw all tracks, the `sendSyntenyRender` test will fail rendering.
// In addition, as the height of tracks may change significantly, we are here too early to guess what will be on screen or not.
export function refreshTracks(trackId?: string) {
  const state = useGenomeViewerStore.getState();
  const baseData = initRenderBaseMessageData();

  // Post message to workers to render with new start
  for (const track of state.tracks.values()) {
    if (trackId != undefined && track.trackId !== trackId) {
      continue;
    }

    const trackGroup = state.trackGroups.get(track.groupId);

    if (!trackGroup) {
      continue;
    }

    if (trackGroup.graphics.isVisible && track.isVisible) {
      postTrackRenderMessage(
        trackGroup,
        track,
        {
          ...baseData,
          activeSyntenyType: state.activeSyntenyType,
          syntenyId: state.syntenyOrganism.id,
          pileupRenderingMode: state.pileupRenderingMode,
        },
        state.syntenyWorker,
      );
    }
  }
}

function postTrackRenderMessage(
  trackGroup: TrackGroup,
  track: Track,
  options: {
    basePerPixel: number;
    width: number;
    scrollX: number;
    activeSyntenyType?: SyntenyTypeEnum;
    syntenyId?: number;
    forceBaseStart?: number;
    pileupRenderingMode: PileupRenderingMode;
  },
  syntenyWorker: Worker | undefined,
) {
  const { busyList, waitingList, tracks } = useGenomeViewerStore.getState();

  if (busyList.has(track.trackId)) {
    waitingList.add(track.trackId);
    return;
  }

  busyList.add(track.trackId);

  function done(event: MessageEvent) {
    const data = event.data;
    if (data.type !== "imageBitmap") return;

    busyList.delete(track.trackId);

    // Worker being synchronous, as long as it returns, it should be done.
    track.worker.removeEventListener("message", done);

    if (waitingList.has(track.trackId)) {
      waitingList.delete(track.trackId);
      refreshTracks(track.trackId);
    }

    const { asynchronousScrolling } = useGenomeViewerStore.getState();

    for (const waitingTrackId of waitingList) {
      if (![...tracks.keys()].includes(waitingTrackId)) {
        waitingList.delete(waitingTrackId);
      }
    }

    for (const busyTrackId of busyList) {
      if (![...tracks.keys()].includes(busyTrackId)) {
        busyList.delete(busyTrackId);
      }
    }

    if (asynchronousScrolling || (!asynchronousScrolling && busyList.size === 0 && waitingList.size === 0)) {
      // We are calling sendSyntenyRenderMessage because all the busy logic is managed here.
      // It should be called in the handleImageBitmap function, but it will be as much confusing as having it here
      // because I didn't find a better way to do it. Honestly. I like neither choices :/
      sendSyntenyRenderMessage(syntenyWorker);
    }
  }

  track.worker.addEventListener("message", done);

  const messageBase: WorkerMessageRenderBase = {
    type: "render",
    token: localStorage.getItem(TOKEN_KEY) ?? "",
    urls: getUrls(),
    trackId: track.trackId,
    contigId: trackGroup.contigId,
    length: trackGroup.graphics.length,
    baseStart:
      options.forceBaseStart ?? Math.floor(options.scrollX * options.basePerPixel - trackGroup.graphics.baseShift),
    basePerPixel: options.basePerPixel,
    mainCanvasWidth: options.width,
  };

  if (track.trackType === TrackEnum.PROTEIN_LOCATION) {
    const syntenyId = options.activeSyntenyType === SyntenyTypeEnum.PROTEIN ? options.syntenyId : undefined;

    track.worker.postMessage({
      ...messageBase,
      text: track.text,
      color: track.color,
      synteny: !!syntenyId,
      syntenyId,
      pileupRenderingMode: options.pileupRenderingMode,
    } satisfies WorkerMessageProtein);
  } else if (track.trackType === TrackEnum.GENE) {
    const syntenyId = options.activeSyntenyType === SyntenyTypeEnum.GENE ? options.syntenyId : undefined;

    track.worker.postMessage({
      ...messageBase,
      text: track.text,
      color: track.color,
      synteny: !!syntenyId,
      syntenyId,
    } satisfies WorkerMessageGene);
  } else if (track.trackType === TrackEnum.NUCLEOTIDE_FEATURE) {
    track.worker.postMessage({
      ...messageBase,
      text: track.text,
      color: track.color,
      synteny: false,
      featureType: track.subType!,
    } satisfies WorkerMessageNUFeature);
  } else if (track.trackType === TrackEnum.SEQUENCE) {
    const syntenyId = options.activeSyntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT ? options.syntenyId : undefined;

    track.worker.postMessage({
      ...messageBase,
      synteny: !!syntenyId,
      syntenyId,
    } satisfies WorkerMessageSequence);
  }
}
